<template>
  <div>
    <h1>Teacher Dashboard</h1>
    <p class="text-muted font-weight-bold">
      Welcome back! Here's how your students are doing.
    </p>
    <hr />

    <!-- Attention Card -->
    <b-card no-body class="border-danger mb-4 overflow-hidden bg-light-danger">
      <b-card-body>
        <div class="d-flex flex-column flex-md-row">
          <div class="mr-md-4 mb-3 mb-md-0">
            <div
              class="rounded bg-light-danger text-danger d-flex align-items-center justify-content-center"
              style="width: 48px; height: 48px"
            >
              <feather-icon icon="AlertTriangleIcon" size="2x" />
            </div>
          </div>
          <div class="flex-grow-1">
            <h4 class="font-weight-bold text-dark">
              3 Students Need Attention
            </h4>
            <p class="text-muted font-weight-bold">
              These students are showing signs of falling behind and may need
              intervention.
            </p>

            <div class="d-flex flex-wrap mb-1">
              <div
                v-for="(student, index) in students"
                :key="index"
                class="bg-white rounded border p-1 mr-3 mb-2 d-flex align-items-center shadow-sm student-pill"
              >
                <b-avatar
                  :text="student.initials"
                  variant="light-danger"
                  class="mr-50 text-danger font-weight-bold"
                  style="background-color: #ffe5e5"
                ></b-avatar>
                <div>
                  <div class="font-weight-bold text-dark">
                    {{ student.name }}
                  </div>
                  <div class="text-muted x-small">
                    {{ student.score }}% overall
                  </div>
                </div>
              </div>
            </div>

            <b-button
              variant="light"
              class="font-weight-bold text-dark border bg-white shadow-sm d-flex align-items-center px-2 py-1 btn-intervention"
            >
              View Intervention Options
              <feather-icon icon="ChevronRightIcon" class="ml-2" />
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Stats Cards -->
    <b-row class="mb-2 hover-card">
      <b-col
        md="6"
        lg="3"
        class="mb-1"
        v-for="(stat, index) in stats"
        :key="index"
      >
        <b-card
          no-body
          class="h-100 shadow-sm overflow-hidden mb-0"
          :style="{ borderLeft: `5px solid ${stat.color}` }"
        >
          <b-card-body>
            <div class="d-flex justify-content-between">
              <div class="flex-grow-1">
                <p
                  class="text-muted font-weight-bold mb-50 small text-uppercase"
                >
                  {{ stat.title }}
                </p>
                <h2 class="font-weight-bolder text-dark">
                  {{ stat.value }}
                </h2>
              </div>
              <div class="ml-3">
                <div
                  class="bg-light text-dark rounded d-flex align-items-center justify-content-center p-75"
                >
                  <feather-icon :icon="stat.icon" size="1.75x" />
                </div>
              </div>
            </div>
            <!-- Stat Details -->
            <div class="mt-1">
              <div
                v-if="stat.subtext"
                :class="
                  stat.trendColor ? `text-${stat.trendColor}` : 'text-muted'
                "
                class="font-weight-bold mb-1"
              >
                <feather-icon :icon="stat.trendIcon" size="14" class="mr-1" />
                {{ stat.subtext }}
                <span
                  v-if="stat.desc"
                  class="text-muted font-weight-normal ml-1"
                  >{{ stat.desc }}</span
                >
              </div>
              <div v-else-if="stat.desc" class="text-muted">
                {{ stat.desc }}
              </div>
              <div v-if="stat.extra" class="text-muted mt-1">
                {{ stat.extra }}
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Charts Row -->
    <b-row class="match-height">
      <!-- Growth Trend Chart -->
      <b-col md="12" lg="8">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <div>
              <b-card-title>Class Growth Trend</b-card-title>
              <b-card-sub-title class="mt-0"
                >Average score vs. target over time</b-card-sub-title
              >
            </div>
          </b-card-header>
          <b-card-body>
            <vue-apex-charts
              type="line"
              height="300"
              :options="growthChartOptions"
              :series="growthChartSeries"
            />
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Performance Distribution Chart -->
      <b-col md="12" lg="4">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <div>
              <b-card-title>Performance Distribution</b-card-title>
              <b-card-sub-title class="mt-0"
                >All students by level</b-card-sub-title
              >
            </div>
          </b-card-header>
          <b-card-body class="d-flex align-items-center justify-content-center">
            <div class="w-100">
              <vue-apex-charts
                type="donut"
                height="250"
                :options="performanceChartOptions"
                :series="performanceChartSeries"
                class="mb-2"
              />
              <!-- Custom Legend -->
              <div class="d-flex flex-column pl-2">
                <div
                  v-for="(item, index) in performanceData"
                  :key="index"
                  class="d-flex justify-content-between align-items-center mb-1"
                >
                  <div class="d-flex align-items-center">
                    <div
                      class="rounded-circle mr-1"
                      :style="{
                        backgroundColor: item.color,
                        width: '10px',
                        height: '10px',
                      }"
                    ></div>
                    <span class="text-muted font-weight-bold small">{{
                      item.label
                    }}</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="font-weight-bold mr-2 small">{{
                      item.count
                    }}</span>
                    <span class="text-muted small">({{ item.pct }})</span>
                  </div>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BCardSubTitle,
  BAvatar,
  BButton,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BCardSubTitle,
    BAvatar,
    BButton,
    VueApexCharts,
  },
  data() {
    return {
      students: [
        { name: "Diego M.", score: 65, initials: "DM" },
        { name: "Jaylen T.", score: 52, initials: "JT" },
        { name: "Zoe D.", score: 68, initials: "ZD" },
      ],
      stats: [
        {
          title: "Total Students",
          value: "24",
          desc: "Across 4 classes",
          color: "#00CFE8", // Cyan/Blue
          icon: "UsersIcon",
        },
        {
          title: "Class Average",
          value: "76.5%",
          subtext: "+3.2%",
          desc: "vs last month",
          color: "#28C76F", // Green
          icon: "TargetIcon",
          trendColor: "success",
          trendIcon: "TrendingUpIcon",
        },
        {
          title: "Proficiency Rate",
          value: "71%",
          subtext: "Meets or Exceeds",
          extra: "+5% vs fall benchmark",
          color: "#28C76F", // Green
          icon: "TrendingUpIcon",
          trendColor: "success",
          trendIcon: "TrendingUpIcon",
        },
        {
          title: "Students At Risk",
          value: "3",
          desc: "Need intervention",
          color: "#EA5455", // Red
          icon: "AlertTriangleIcon",
        },
      ],
      // Graphs
      growthChartSeries: [
        {
          name: "Class Average",
          data: [66, 68, 73, 76, 78],
        },
        {
          name: "Growth Target",
          data: [69, 70, 72, 74, 76],
        },
      ],
      growthChartOptions: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#1e1e2d", "#a0a0a0"],
        stroke: {
          curve: "smooth",
          dashArray: [0, 5],
          width: [3, 2],
        },
        markers: {
          size: 5,
          hover: { size: 7 },
        },
        xaxis: {
          categories: ["Sep", "Oct", "Nov", "Dec", "Jan"],
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 60,
          max: 90,
          tickAmount: 3,
          labels: {
            formatter: (val) => `${val}%`,
          },
        },
        grid: {
          borderColor: "#f1f1f1",
          strokeDashArray: 4,
          yaxis: { lines: { show: true } },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
        },
      },
      performanceChartSeries: [6, 11, 5, 2],
      performanceChartOptions: {
        labels: ["Exceeds", "Meets", "Approaching", "Below"],
        colors: ["#28C76F", "#00CFE8", "#FF9F43", "#EA5455"],
        chart: {
          type: "donut",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "14px",
                  offsetY: 20,
                  color: "#6e6b7b",
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: "bold",
                  offsetY: -20,
                  formatter: () => "71%", // Static for demo matching
                  color: "#1e1e2d",
                },
                total: {
                  show: true,
                  label: "Proficient",
                  color: "#6e6b7b",
                  formatter: () => "71%",
                },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { width: 0 },
      },
      performanceData: [
        { label: "Exceeds", count: 6, pct: "25%", color: "#28C76F" },
        { label: "Meets", count: 11, pct: "46%", color: "#00CFE8" },
        { label: "Approaching", count: 5, pct: "21%", color: "#FF9F43" },
        { label: "Below", count: 2, pct: "8%", color: "#EA5455" },
      ],
    };
  },
};
</script>

<style scoped>
.x-small {
  font-size: 0.75rem;
}

.student-pill {
  min-width: 180px;
  transition: all 0.2s ease-in-out;
}

.student-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.icon-box {
  width: 48px;
  height: 48px;
  background-color: #f8f9fa;
}

.btn-intervention:hover {
  background-color: #f8f9fa !important;
}
</style>
